import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment-timezone';

type ReportData = any;

function flattenRows(report: ReportData) {
  const rows: any[] = [];
  (report.weeks || []).forEach((week: any) => {
    week.rows.forEach((row: any) => {
      const dates = Object.keys(row.days).sort();
      dates.forEach((date) => {
        const cell = row.days[date];
        if (!cell) return;
        rows.push({
          weekStart: week.weekStart,
          employee: row.employeeName,
          employeeId: row.employeeIdCode,
          campaign: row.campaignName,
          supervisor: row.supervisorName || '',
          date,
          day: cell.dayOfWeek,
          schedule: cell.scheduleStart && cell.scheduleEnd ? `${cell.scheduleStart} - ${cell.scheduleEnd}` : '',
          clockIn: cell.clockIn || '',
          clockOut: cell.clockOut || '',
          attendance: cell.attendanceLabel,
          coverage: cell.coverageStatus,
          coverageMode: cell.coverageModeLabel || '',
          hoursWorked: cell.hoursWorked,
          coverageHours: cell.coverageHours,
          originalCampaign: cell.originalCampaignName || '',
          workedCampaign: cell.workedCampaignName || '',
          customerNotified: cell.customerNotified ? 'Yes' : 'No',
          billable: cell.billable ? 'Yes' : 'No',
          billingHours: cell.billingHours,
          invoiceStatus: cell.invoiceStatus,
          rowId: cell.rowId,
        });
      });
    });
  });
  return rows;
}

export function exportWeeklyStaffingCoverageToCSV(report: ReportData) {
  const rows = flattenRows(report);
  const headers = [
    'Week Start', 'Employee', 'Employee ID', 'Campaign', 'Supervisor', 'Date', 'Day',
    'Schedule', 'Clock In', 'Clock Out', 'Attendance', 'Coverage', 'Coverage Mode',
    'Hours Worked', 'Coverage Hours', 'Original Campaign', 'Worked Campaign',
    'Customer Notified', 'Billable', 'Billing Hours', 'Invoice Status', 'Row ID',
  ];
  const csv = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => {
      const key = h.toLowerCase().replace(/ /g, '');
      const map: Record<string, string> = {
        weekstart: 'weekStart', employee: 'employee', employeeid: 'employeeId',
        campaign: 'campaign', supervisor: 'supervisor', date: 'date', day: 'day',
        schedule: 'schedule', clockin: 'clockIn', clockout: 'clockOut',
        attendance: 'attendance', coverage: 'coverage', coveragemode: 'coverageMode',
        hoursworked: 'hoursWorked', coveragehours: 'coverageHours',
        originalcampaign: 'originalCampaign', workedcampaign: 'workedCampaign',
        customernotified: 'customerNotified', billable: 'billable',
        billinghours: 'billingHours', invoicestatus: 'invoiceStatus', rowid: 'rowId',
      };
      const val = r[map[key] || key] ?? '';
      return `"${String(val).replace(/"/g, '""')}"`;
    }).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `weekly-staffing-coverage_${report.dateRange.startDate}_${report.dateRange.endDate}.csv`;
  link.click();
}

export function exportWeeklyStaffingCoverageToExcel(report: ReportData) {
  const rows = flattenRows(report);
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Workforce Activity');

  const summary = [
    ['Weekly Staffing & Coverage Report'],
    ['Subtitle', report.subtitle],
    ['Date Range', `${report.dateRange.startDate} to ${report.dateRange.endDate}`],
    ['Scheduled Hours', report.summary.scheduledHours],
    ['Worked Hours', report.summary.workedHours],
    ['Coverage Hours', report.summary.coverageHours],
    ['Billable Hours', report.summary.billableHours],
  ];
  const wsSummary = XLSX.utils.aoa_to_sheet(summary);
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

  XLSX.writeFile(wb, `weekly-staffing-coverage_${report.dateRange.startDate}_${report.dateRange.endDate}.xlsx`);
}

export function exportWeeklyStaffingCoverageToPDF(report: ReportData) {
  const doc = new jsPDF({ orientation: 'landscape' });
  doc.setFontSize(16);
  doc.text(report.reportName, 14, 18);
  doc.setFontSize(10);
  doc.text(report.subtitle, 14, 26);
  doc.text(
    `Period: ${moment(report.dateRange.startDate).format('MMM D, YYYY')} – ${moment(report.dateRange.endDate).format('MMM D, YYYY')}`,
    14,
    32,
  );

  const summaryBody = [
    ['Scheduled Hours', String(report.summary.scheduledHours)],
    ['Worked Hours', String(report.summary.workedHours)],
    ['Coverage Hours', String(report.summary.coverageHours)],
    ['Billable Hours', String(report.summary.billableHours)],
    ['Customer Notifications', String(report.summary.customerNotifications)],
    ['Operational Readiness', `${report.summary.operationalReadinessAverage}%`],
  ];
  autoTable(doc, {
    startY: 38,
    head: [['Metric', 'Value']],
    body: summaryBody,
    theme: 'grid',
    styles: { fontSize: 9 },
  });

  const rows = flattenRows(report);
  autoTable(doc, {
    startY: (doc as any).lastAutoTable.finalY + 8,
    head: [[
      'Employee', 'Campaign', 'Date', 'Schedule', 'In', 'Out', 'Attendance',
      'Coverage', 'Worked Hrs', 'Billable Hrs', 'Worked Campaign', 'Notified', 'Billable',
    ]],
    body: rows.map((r) => [
      r.employee, r.campaign, r.date, r.schedule, r.clockIn, r.clockOut,
      r.attendance, r.coverage, r.hoursWorked, r.billingHours, r.workedCampaign,
      r.customerNotified, r.billable,
    ]),
    styles: { fontSize: 7 },
    headStyles: { fillColor: [21, 101, 192] },
  });

  doc.save(`weekly-staffing-coverage_${report.dateRange.startDate}_${report.dateRange.endDate}.pdf`);
}

export function printWeeklyStaffingCoverageReport(report: ReportData) {
  exportWeeklyStaffingCoverageToPDF(report);
}
